import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES_PATH } from '@/shared/constants/routes';
import {
  AiAssistantConfigDto,
  CompanyModuleBillingStateDto,
  CompanyModuleKeyDto,
  CompanyModuleStatusDto,
  SafeAutomationSourceDto,
} from '@/shared/api/companies';
import type { CompanyModuleDto } from '@/shared/api/companies';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Notification } from '@/shared/ui';

import {
  useCompanyModules,
  useUpdateCompanyModule,
} from '../model/useCompanyModules';
import { SafeAutomationConfigPanel } from './SafeAutomationConfigPanel';
import { AiProviderConfigPanel } from './AiProviderConfigPanel';
import { AiAssistantPolicyPanel } from './AiAssistantPolicyPanel';
import { AiAuditPanel } from './AiAuditPanel';
import { KnowledgeBaseModuleSummary } from './KnowledgeBaseModuleSummary';
import { AiAnalyticsPanel } from './AiAnalyticsPanel';

const MODULE_ICONS: Record<CompanyModuleKeyDto, SvgIconComponent> = {
  crm_messenger_core: HubOutlinedIcon,
  safe_automation: ShieldOutlinedIcon,
  ai_assistant: AutoAwesomeOutlinedIcon,
  knowledge_base: MenuBookOutlinedIcon,
  campaigns: CampaignOutlinedIcon,
  analytics: InsightsOutlinedIcon,
};

const AI_MODULE_KEYS = new Set<CompanyModuleKeyDto>([
  'safe_automation',
  'ai_assistant',
  'knowledge_base',
  'campaigns',
  'analytics',
]);

const AI_RAIL_LABEL_KEYS: Partial<Record<CompanyModuleKeyDto, string>> = {
  safe_automation: 'company:modules.rail.safeAutomation',
  ai_assistant: 'company:modules.rail.aiAssistant',
  knowledge_base: 'company:modules.rail.knowledgeBase',
  campaigns: 'company:modules.rail.aiCampaigns',
  analytics: 'company:modules.rail.aiAnalytics',
};

const COMMAND_MODULE_KEYS: CompanyModuleKeyDto[] = [
  'crm_messenger_core',
  'safe_automation',
  'ai_assistant',
  'knowledge_base',
  'campaigns',
  'analytics',
];

const COMMAND_STEP_LABEL_KEYS: Partial<Record<CompanyModuleKeyDto, string>> = {
  ...AI_RAIL_LABEL_KEYS,
};

const MODULE_ACCENTS: Record<CompanyModuleKeyDto, string> = {
  crm_messenger_core: '#0078D4',
  safe_automation: '#1B8A63',
  ai_assistant: '#B87912',
  knowledge_base: '#2473B7',
  campaigns: '#FF7A59',
  analytics: '#519C66',
};

const MODULE_GROUPS = [
  {
    key: 'foundation',
    titleKey: 'company:modules.groups.foundation.title',
    captionKey: 'company:modules.groups.foundation.caption',
    modules: ['crm_messenger_core'] as CompanyModuleKeyDto[],
  },
  {
    key: 'aiWorkflow',
    titleKey: 'company:modules.groups.aiWorkflow.title',
    captionKey: 'company:modules.groups.aiWorkflow.caption',
    modules: [
      'safe_automation',
      'ai_assistant',
      'knowledge_base',
      'campaigns',
      'analytics',
    ] as CompanyModuleKeyDto[],
  },
];

const STATUS_COLOR: Record<
  CompanyModuleStatusDto,
  'success' | 'warning' | 'default'
> = {
  enabled: 'success',
  disabled: 'default',
  billing_required: 'warning',
  misconfigured: 'warning',
};

const MODULE_CHIP_SX = {
  height: 'auto',
  minHeight: 24,
  '& .MuiChip-label': {
    display: 'block',
    lineHeight: 1.3,
    overflow: 'visible',
    py: 0.25,
    textOverflow: 'clip',
    whiteSpace: 'normal',
  },
};

type CommandSeverity = 'critical' | 'desirable' | 'info' | 'ready';
type CommandAction = 'fix' | 'configure' | 'open';
type CommandTargetKey = CompanyModuleKeyDto | 'ai_audit' | 'analytics_page';

const SAFE_AUTOMATION_SOURCES: SafeAutomationSourceDto[] = [
  'amocrm_digital_pipeline',
  'ai_assistant',
  'campaign',
];
const AI_ASSISTANT_SAFE_AUTOMATION_SOURCE: SafeAutomationSourceDto =
  'ai_assistant';

function withAiAssistantSafeAutomationSource(config: Record<string, unknown>) {
  const enabledSources = Array.isArray(config.enabled_sources)
    ? config.enabled_sources.filter(
        (source): source is SafeAutomationSourceDto =>
          SAFE_AUTOMATION_SOURCES.includes(source as SafeAutomationSourceDto)
      )
    : [];

  if (!enabledSources.includes(AI_ASSISTANT_SAFE_AUTOMATION_SOURCE)) {
    enabledSources.push(AI_ASSISTANT_SAFE_AUTOMATION_SOURCE);
  }

  return {
    ...config,
    enabled_sources: enabledSources,
  };
}

function hasAiAssistantSafeAutomationSource(config: Record<string, unknown>) {
  return (
    Array.isArray(config.enabled_sources) &&
    config.enabled_sources.includes(AI_ASSISTANT_SAFE_AUTOMATION_SOURCE)
  );
}

function getModuleStatusLabelKey(module: {
  status: CompanyModuleStatusDto;
  billing_state: CompanyModuleBillingStateDto;
}) {
  if (module.status === 'disabled' && module.billing_state !== 'unavailable') {
    return 'company:modules.status.off';
  }

  return `company:modules.status.${module.status}`;
}

function getModuleConfigurationHintKey(module: {
  status: CompanyModuleStatusDto;
  billing_state: CompanyModuleBillingStateDto;
}) {
  if (module.billing_state === 'unavailable')
    return 'company:modules.setup.unavailable';
  if (module.status === 'billing_required')
    return 'company:modules.setup.billingRequired';
  if (module.status === 'disabled')
    return 'company:modules.setup.enableToConfigure';
  return '';
}

function getModuleActionHintKey(module: {
  key: CompanyModuleKeyDto;
  status: CompanyModuleStatusDto;
  billing_state: CompanyModuleBillingStateDto;
  setup: { missing: string[] };
}) {
  if (module.billing_state === 'unavailable')
    return 'company:modules.nextStep.unavailable';
  if (module.status === 'billing_required')
    return 'company:modules.nextStep.billingRequired';
  if (module.key === 'campaigns' && module.status === 'enabled')
    return 'company:modules.nextStep.campaignsEnabledProfileOnly';
  if (module.setup.missing.includes('channels'))
    return 'company:modules.nextStep.connectChannel';
  if (module.status === 'misconfigured')
    return 'company:modules.nextStep.finishSetup';
  if (module.status === 'disabled') return 'company:modules.nextStep.enable';
  return 'company:modules.nextStep.ready';
}

function getModuleStatusIcon(module: {
  status: CompanyModuleStatusDto;
  billing_state: CompanyModuleBillingStateDto;
}) {
  if (module.billing_state === 'unavailable') return LockOutlinedIcon;
  if (module.status === 'enabled') return CheckCircleOutlineOutlinedIcon;
  if (module.status === 'billing_required' || module.status === 'misconfigured')
    return ErrorOutlineOutlinedIcon;
  return RadioButtonUncheckedOutlinedIcon;
}

function getCommandSeverity(module?: CompanyModuleDto): CommandSeverity {
  if (!module) return 'info';
  if (
    module.status === 'billing_required' ||
    module.status === 'misconfigured' ||
    module.setup.missing.length > 0
  )
    return 'critical';
  if (module.status === 'disabled' && module.billing_state !== 'unavailable')
    return 'desirable';
  if (module.billing_state === 'unavailable') return 'info';
  return 'ready';
}

function getCommandAction(severity: CommandSeverity): CommandAction {
  if (severity === 'critical') return 'fix';
  if (severity === 'desirable') return 'configure';
  return 'open';
}

export const CompanyModulesSettings = ({
  companyId,
  canManageModules = false,
}: {
  companyId: string;
  canManageModules?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const moduleRefs = useRef<
    Partial<Record<CommandTargetKey, HTMLElement | null>>
  >({});
  const { data, isPending, isError, error } = useCompanyModules(companyId);
  const updateModuleMutation = useUpdateCompanyModule(companyId);
  const modules = data?.modules ?? [];
  const commandSteps = useMemo(() => {
    return COMMAND_MODULE_KEYS.map((key) => {
      const module = modules.find((item) => item.key === key);
      const title = t(`company:modules.catalog.${key}.title`, {
        defaultValue: module?.title ?? key,
      });
      const severity = getCommandSeverity(module);
      const action = getCommandAction(severity);
      const targetKey: CommandTargetKey =
        key === 'analytics' && !module ? 'analytics_page' : key;
      const workflowIndex = COMMAND_MODULE_KEYS.indexOf(key);
      const labelPrefix =
        key === 'crm_messenger_core' ? '' : `${workflowIndex}. `;

      return {
        key,
        title,
        label: `${labelPrefix}${t(
          COMMAND_STEP_LABEL_KEYS[key] ?? `company:modules.catalog.${key}.title`,
          { defaultValue: title }
        )}`,
        severity,
        action,
        reason: module
          ? t(getModuleActionHintKey(module), { module: title })
          : '-',
        targetKey,
        secondaryAction:
          key === 'ai_assistant' && module
            ? {
                label: t('company:modules.command.aiAudit'),
                targetKey: 'ai_audit' as CommandTargetKey,
              }
            : undefined,
      };
    });
  }, [modules, t]);

  if (isPending) return <Loader containerSize="small" />;

  if (isError) {
    return (
      <>
        <NotFoundData title={t('company:modules.loadError')} />
        <Notification open={isError} title={error?.message} />
      </>
    );
  }

  const enabledCount = modules.filter(
    (module) => module.status === 'enabled'
  ).length;
  const availableModules = modules.filter(
    (module) => module.billing_state !== 'unavailable'
  );
  const enabledAvailableCount = availableModules.filter(
    (module) => module.status === 'enabled'
  ).length;
  const attentionCount = modules.filter(
    (module) =>
      module.status === 'billing_required' ||
      module.status === 'misconfigured' ||
      module.setup.missing.length > 0
  ).length;
  const availableCount = availableModules.length;
  const nextModule =
    modules.find(
      (module) =>
        module.status === 'misconfigured' ||
        module.setup.missing.length > 0 ||
        module.status === 'billing_required'
    ) ??
    modules.find(
      (module) =>
        module.status === 'disabled' &&
        module.billing_state !== 'unavailable' &&
        !module.core
    );
  const moduleProgress = availableCount
    ? Math.round((enabledAvailableCount / availableCount) * 100)
    : 0;
  const isCommandCompact = attentionCount === 0 && moduleProgress === 100;
  const highlightedStepKey = commandSteps.find(
    (step) => step.severity === 'critical' || step.severity === 'desirable'
  )?.key;
  const nextModuleTitle = nextModule
    ? t(`company:modules.catalog.${nextModule.key}.title`, {
        defaultValue: nextModule.title,
      })
    : '';
  const NextModuleStatusIcon = nextModule
    ? getModuleStatusIcon(nextModule)
    : CheckCircleOutlineOutlinedIcon;
  const safeAutomationModule = modules.find(
    (module) => module.key === 'safe_automation'
  );
  const aiAssistantModule = modules.find(
    (module) => module.key === 'ai_assistant'
  );
  const scrollToCommandTarget = (targetKey: CommandTargetKey) => {
    const targetNode = moduleRefs.current[targetKey];

    if (targetNode) {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (targetKey === 'analytics_page') {
      navigate(`/${ROUTES_PATH.MAIN.ANALYTICS}`);
    }
  };
  const openCampaignWorkspace = () => {
    navigate(`/${ROUTES_PATH.MAIN.CAMPAIGNS.replace(':id', companyId)}`);
  };
  const saveAiAssistantPolicy = async ({
    config,
    scope,
  }: {
    config: AiAssistantConfigDto;
    scope: { channel_ids: string[] };
  }) => {
    if (
      config.mode === 'delayed_autonomous' &&
      safeAutomationModule &&
      safeAutomationModule.billing_state !== 'unavailable' &&
      (safeAutomationModule.status !== 'enabled' ||
        !hasAiAssistantSafeAutomationSource(safeAutomationModule.config))
    ) {
      await updateModuleMutation.mutateAsync({
        moduleKey: 'safe_automation',
        data: {
          status: 'enabled',
          scope: safeAutomationModule.scope,
          config: withAiAssistantSafeAutomationSource(
            safeAutomationModule.config
          ),
        },
      });
    }

    await updateModuleMutation.mutateAsync({
      moduleKey: 'ai_assistant',
      data: {
        status:
          aiAssistantModule?.status === 'enabled' ? 'enabled' : 'disabled',
        scope,
        config,
      },
    });
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.common.black, 0.08),
          borderRadius: 1,
          bgcolor: 'background.paper',
          boxShadow: '0 18px 44px rgba(28, 29, 34, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: {
              xs: isCommandCompact ? 1.5 : 2,
              md: isCommandCompact ? 2 : 2.5,
            },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
            gap: {
              xs: isCommandCompact ? 1.5 : 2,
              md: isCommandCompact ? 2 : 2.5,
            },
            alignItems: 'stretch',
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.055
              )} 0%, ${alpha(MODULE_ACCENTS.safe_automation, 0.045)} 48%, ${
                theme.palette.common.white
              } 100%)`,
          }}
        >
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={850} letterSpacing={0}>
                {t('company:modules.title')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 780 }}
              >
                {isCommandCompact
                  ? t('company:modules.command.compactReady')
                  : t('company:modules.caption')}
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {[
                {
                  value: `${enabledCount}/${modules.length}`,
                  label: t('company:modules.summary.enabled'),
                  color: 'actions.go',
                },
                {
                  value: availableCount,
                  label: t('company:modules.summary.available'),
                  color: 'primary.main',
                },
                {
                  value: attentionCount,
                  label: t('company:modules.summary.attention'),
                  color: attentionCount > 0 ? 'warning.main' : 'text.secondary',
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    minWidth: 0,
                    minHeight: isCommandCompact ? 62 : 76,
                    px: 1.5,
                    py: isCommandCompact ? 1 : 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid',
                    borderColor: (theme) =>
                      alpha(theme.palette.common.black, 0.08),
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    sx={{
                      color: item.color,
                      fontSize: { xs: 20, md: 23 },
                      fontWeight: 850,
                      lineHeight: 1,
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mt: 0.6,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>

          <Box
            sx={{
              minWidth: 0,
              p: isCommandCompact ? 1.25 : 1.5,
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.common.black, 0.08),
              borderRadius: 1,
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: '0 10px 24px rgba(28, 29, 34, 0.06)',
            }}
          >
            <Stack spacing={1.35}>
              <Stack direction="row" spacing={1.1} alignItems="flex-start">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 1,
                    color: nextModule ? 'warning.main' : 'actions.go',
                    bgcolor: (theme) =>
                      alpha(
                        nextModule
                          ? theme.palette.warning.main
                          : theme.palette.actions.go,
                        0.12
                      ),
                  }}
                >
                  <NextModuleStatusIcon fontSize="small" />
                </Box>
                <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                  <Typography fontWeight={850} sx={{ fontSize: 14 }}>
                    {t('company:modules.nextStep.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {nextModule
                      ? t(getModuleActionHintKey(nextModule), {
                          module: nextModuleTitle,
                        })
                      : t('company:modules.nextStep.allReady')}
                  </Typography>
                </Stack>
              </Stack>

              <Divider />

              <Stack spacing={0.85}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 700 }}
                  >
                    {t('company:modules.summary.progress')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 700 }}
                  >
                    {moduleProgress}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={moduleProgress}
                  sx={{
                    height: 7,
                    borderRadius: 1,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                      bgcolor:
                        attentionCount > 0 ? 'warning.main' : 'actions.go',
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: isCommandCompact ? 1.35 : 2,
            borderTop: '1px solid',
            borderColor: (theme) => alpha(theme.palette.common.black, 0.08),
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.018),
          }}
        >
          <Stack spacing={1.35}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={0.75}
              justifyContent="space-between"
            >
              <Typography fontWeight={850} sx={{ fontSize: 14 }}>
                {t('company:modules.workflow.title')}
              </Typography>
              {isCommandCompact ? null : (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', maxWidth: 560 }}
                >
                  {t('company:modules.command.expandedCaption')}
                </Typography>
              )}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(6, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {commandSteps.map((step) => {
                const Icon = MODULE_ICONS[step.key];
                const isHighlighted = highlightedStepKey === step.key;
                const secondaryAction = step.secondaryAction;
                const severityColor =
                  step.severity === 'critical'
                    ? '#D14343'
                    : step.severity === 'desirable'
                      ? '#B87912'
                      : step.severity === 'ready'
                        ? '#1B8A63'
                        : '#607080';
                const accent = MODULE_ACCENTS[step.key];

                return (
                  <Box key={step.key} sx={{ minWidth: 0 }}>
                    <ButtonBase
                      onClick={() => scrollToCommandTarget(step.targetKey)}
                      focusRipple
                      sx={{
                        width: '100%',
                        minWidth: 0,
                        minHeight: isCommandCompact ? 84 : 112,
                        p: isCommandCompact ? 0.9 : 1,
                        display: 'grid',
                        gridTemplateColumns: '30px minmax(0, 1fr)',
                        gap: 1,
                        alignItems: 'stretch',
                        textAlign: 'left',
                        border: '1px solid',
                        borderColor: isHighlighted
                          ? alpha(severityColor, 0.42)
                          : step.severity === 'ready'
                            ? alpha(accent, 0.28)
                            : (theme) =>
                                alpha(theme.palette.common.black, 0.08),
                        borderRadius: 1,
                        bgcolor: isHighlighted
                          ? alpha(severityColor, 0.055)
                          : 'background.paper',
                        color: 'inherit',
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: 1,
                          color: severityColor,
                          bgcolor: alpha(severityColor, 0.1),
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>

                      <Stack spacing={0.55} sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={0.7}
                          alignItems="flex-start"
                          justifyContent="space-between"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 850,
                              lineHeight: 1.15,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {step.label}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              px: 0.65,
                              py: 0.2,
                              flexShrink: 0,
                              borderRadius: 0.75,
                              bgcolor: alpha(severityColor, 0.11),
                              color: severityColor,
                              fontSize: 10.5,
                              fontWeight: 850,
                              lineHeight: 1.25,
                            }}
                          >
                            {t(
                              `company:modules.command.severity.${step.severity}`
                            )}
                          </Typography>
                        </Stack>

                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: isCommandCompact ? 1 : 2,
                          }}
                        >
                          {step.reason}
                        </Typography>

                        <Typography
                          component="span"
                          sx={{
                            mt: 'auto',
                            color: severityColor,
                            fontSize: 11.5,
                            fontWeight: 850,
                            lineHeight: 1.2,
                          }}
                        >
                          {t(`company:modules.command.actions.${step.action}`)}
                        </Typography>
                      </Stack>
                    </ButtonBase>

                    {secondaryAction ? (
                      <ButtonBase
                        onClick={() =>
                          scrollToCommandTarget(secondaryAction.targetKey)
                        }
                        focusRipple
                        sx={{
                          mt: 0.65,
                          width: '100%',
                          minHeight: 28,
                          px: 1,
                          py: 0.55,
                          justifyContent: 'flex-start',
                          border: '1px solid',
                          borderColor: alpha(MODULE_ACCENTS.ai_assistant, 0.2),
                          borderRadius: 1,
                          color: MODULE_ACCENTS.ai_assistant,
                          bgcolor: 'background.paper',
                          fontSize: 12,
                          fontWeight: 800,
                          textAlign: 'left',
                          '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {secondaryAction.label}
                      </ButtonBase>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </Box>
      </Box>

      {aiAssistantModule && canManageModules ? (
        <Box id="ai-provider-settings" sx={{ mb: 2.5 }}>
          <AiProviderConfigPanel
            accentColor={MODULE_ACCENTS.ai_assistant}
            disabled={!canManageModules || updateModuleMutation.isPending}
          />
        </Box>
      ) : null}

      <Stack spacing={2.5}>
        {MODULE_GROUPS.map((group) => {
          const groupModules = modules.filter((module) =>
            group.modules.includes(module.key)
          );

          if (!groupModules.length) return null;

          return (
            <Stack key={group.key} spacing={1.25}>
              <Stack spacing={0.3}>
                <Typography fontWeight={850} sx={{ fontSize: 16 }}>
                  {t(group.titleKey)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(group.captionKey)}
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridAutoFlow: { xl: 'row dense' },
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {groupModules.map((module) => {
                  const Icon = MODULE_ICONS[module.key];
                  const accent = MODULE_ACCENTS[module.key];
                  const StatusIcon = getModuleStatusIcon(module);
                  const isAiModule = AI_MODULE_KEYS.has(module.key);
                  const isKnowledgeBaseModule = module.key === 'knowledge_base';
                  const missingChannels =
                    module.setup.missing.includes('channels');
                  const isConfigurationAvailable =
                    module.billing_state !== 'unavailable' &&
                    (module.status === 'enabled' ||
                      module.status === 'misconfigured');
                  const configurationHintKey =
                    getModuleConfigurationHintKey(module);
                  const configurationDisabled =
                    !canManageModules ||
                    updateModuleMutation.isPending ||
                    !isConfigurationAvailable;
                  const canToggle =
                    canManageModules &&
                    !module.core &&
                    module.billing_state !== 'unavailable';
                  const showBillingChip =
                    module.billing_state !== 'unavailable';
                  const title = t(
                    `company:modules.catalog.${module.key}.title`,
                    {
                      defaultValue: module.title,
                    }
                  );
                  const description = t(
                    `company:modules.catalog.${module.key}.description`,
                    {
                      defaultValue: module.description,
                    }
                  );

                  return (
                    <Box
                      key={module.key}
                      ref={(node: HTMLDivElement | null) => {
                        moduleRefs.current[module.key] = node;
                      }}
                      sx={{
                        position: 'relative',
                        minWidth: 0,
                        minHeight: isAiModule
                          ? isKnowledgeBaseModule
                            ? 188
                            : 214
                          : 178,
                        p: isAiModule ? 2.25 : 2,
                        gridColumn:
                          module.key === 'safe_automation' ||
                          module.key === 'ai_assistant'
                            ? { xs: 'auto', md: 'span 2', xl: 'span 2' }
                            : 'auto',
                        border: '1px solid',
                        borderColor: isAiModule
                          ? alpha(
                              accent,
                              module.status === 'enabled' ? 0.26 : 0.16
                            )
                          : module.status === 'enabled'
                            ? (theme) => alpha(theme.palette.actions.go, 0.26)
                            : 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                        color: 'inherit',
                        boxShadow: isAiModule
                          ? '0 14px 34px rgba(28, 29, 34, 0.06)'
                          : '0 12px 30px rgba(28, 29, 34, 0.04)',
                        '&::before': isAiModule
                          ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              left: 0,
                              height: 3,
                              pointerEvents: 'none',
                              backgroundColor: alpha(
                                accent,
                                module.status === 'enabled' ? 0.86 : 0.24
                              ),
                            }
                          : undefined,
                      }}
                    >
                      <Stack
                        spacing={1.4}
                        sx={{ position: 'relative', zIndex: 1 }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                          justifyContent="space-between"
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.25}
                            sx={{ minWidth: 0 }}
                          >
                            <Box
                              sx={{
                                width: isAiModule ? 44 : 36,
                                height: isAiModule ? 44 : 36,
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                border: '1px solid',
                                borderColor: isAiModule
                                  ? alpha(accent, 0.28)
                                  : 'transparent',
                                borderRadius: 1,
                                color: isAiModule
                                  ? accent
                                  : module.status === 'enabled'
                                    ? 'actions.go'
                                    : 'text.secondary',
                                bgcolor: isAiModule
                                  ? alpha(accent, 0.08)
                                  : module.status === 'enabled'
                                    ? (theme) =>
                                        alpha(theme.palette.actions.go, 0.1)
                                    : 'action.hover',
                              }}
                            >
                              <Icon
                                fontSize={isAiModule ? 'medium' : 'small'}
                              />
                            </Box>

                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                              <Typography
                                fontWeight={800}
                                sx={{
                                  color: 'inherit',
                                  letterSpacing: 0,
                                }}
                              >
                                {title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  maxWidth: isAiModule ? 640 : 'none',
                                }}
                              >
                                {description}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Chip
                            size="small"
                            color={STATUS_COLOR[module.status]}
                            icon={<StatusIcon />}
                            label={t(getModuleStatusLabelKey(module))}
                            sx={{
                              ...MODULE_CHIP_SX,
                              flexShrink: 0,
                              alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                              borderRadius: 1,
                              bgcolor: isAiModule
                                ? alpha(
                                    accent,
                                    module.status === 'enabled' ? 0.1 : 0.06
                                  )
                                : undefined,
                              border: isAiModule ? '1px solid' : undefined,
                              borderColor: isAiModule
                                ? alpha(accent, 0.2)
                                : undefined,
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            px: 1.25,
                            py: 1,
                            border: '1px solid',
                            borderColor: (theme) =>
                              alpha(theme.palette.common.black, 0.08),
                            borderRadius: 1,
                            bgcolor: (theme) =>
                              alpha(
                                accent,
                                module.status === 'enabled' ? 0.07 : 0.035
                              ),
                            color:
                              module.status === 'enabled'
                                ? 'text.primary'
                                : 'text.secondary',
                          }}
                        >
                          {t(getModuleActionHintKey(module), { module: title })}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {module.core ? (
                            <Chip
                              size="small"
                              label={t('company:modules.labels.core')}
                              sx={{
                                ...MODULE_CHIP_SX,
                                borderRadius: 1,
                              }}
                            />
                          ) : null}
                          {showBillingChip ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t(
                                `company:modules.billing.${module.billing_state}`
                              )}
                              sx={{
                                ...MODULE_CHIP_SX,
                                borderRadius: 1,
                                color: isAiModule
                                  ? 'text.secondary'
                                  : undefined,
                                borderColor: isAiModule
                                  ? (theme) =>
                                      alpha(theme.palette.text.primary, 0.18)
                                  : undefined,
                              }}
                            />
                          ) : null}
                          {module.scope.channel_ids.length ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('company:modules.labels.channels', {
                                count: module.scope.channel_ids.length,
                              })}
                              sx={{
                                ...MODULE_CHIP_SX,
                                borderRadius: 1,
                                color: isAiModule
                                  ? 'text.secondary'
                                  : undefined,
                                borderColor: isAiModule
                                  ? (theme) =>
                                      alpha(theme.palette.text.primary, 0.18)
                                  : undefined,
                              }}
                            />
                          ) : null}
                        </Stack>

                        {missingChannels ? (
                          <Typography
                            variant="body2"
                            sx={{ color: 'warning.main' }}
                          >
                            {t('company:modules.setup.needsChannels')}
                          </Typography>
                        ) : null}

                        {canToggle ? (
                          <FormControlLabel
                            sx={{
                              m: 0,
                              alignSelf: 'flex-start',
                              '& .MuiFormControlLabel-label': {
                                fontSize: 14,
                                lineHeight: 1.35,
                              },
                            }}
                            control={
                              <Switch
                                size="small"
                                checked={module.status === 'enabled'}
                                disabled={updateModuleMutation.isPending}
                                onChange={(event) => {
                                  const shouldUseDefaultScope =
                                    module.key === 'ai_assistant' &&
                                    event.target.checked &&
                                    module.scope.channel_ids.length === 0;
                                  updateModuleMutation.mutate({
                                    moduleKey: module.key,
                                    data: {
                                      status: event.target.checked
                                        ? 'enabled'
                                        : 'disabled',
                                      ...(shouldUseDefaultScope
                                        ? {}
                                        : { scope: module.scope }),
                                      config: module.config,
                                    },
                                  });
                                }}
                              />
                            }
                            label={
                              module.status === 'enabled'
                                ? t('company:modules.actions.enabled')
                                : t('company:modules.actions.disabled')
                            }
                          />
                        ) : null}

                        {module.key === 'campaigns' &&
                        module.status === 'enabled' ? (
                          <Stack
                            spacing={1}
                            sx={{
                              px: 1.25,
                              py: 1,
                              border: '1px solid',
                              borderColor: alpha(accent, 0.22),
                              borderRadius: 1,
                              bgcolor: alpha(accent, 0.035),
                            }}
                          >
                            <Typography variant="body2" fontWeight={800}>
                              {t(
                                'company:modules.nextStep.campaignsEnabledProfileOnly'
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('company:modules.campaigns.workspaceHint')}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenInNewOutlinedIcon />}
                              onClick={openCampaignWorkspace}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              {t('company:modules.campaigns.openWorkspace')}
                            </Button>
                          </Stack>
                        ) : null}

                        {module.key === 'analytics' ? (
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            sx={{
                              px: 1.25,
                              py: 1,
                              border: '1px solid',
                              borderColor: alpha(accent, 0.22),
                              borderRadius: 1,
                              bgcolor: alpha(accent, 0.035),
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: 1 }}
                            >
                              {t('company:modules.analytics.enabledCaption')}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                navigate(
                                  `/${ROUTES_PATH.MAIN.ANALYTICS}?tab=ai&company_id=${companyId}`
                                )
                              }
                            >
                              {t('company:modules.analytics.open')}
                            </Button>
                          </Stack>
                        ) : null}

                        {module.key === 'knowledge_base' ? (
                          <KnowledgeBaseModuleSummary
                            companyId={companyId}
                            status={module.status}
                            accentColor={accent}
                            disabled={module.billing_state === 'unavailable'}
                          />
                        ) : null}

                        {isAiModule &&
                        !isKnowledgeBaseModule &&
                        configurationHintKey ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              px: 1.25,
                              py: 1,
                              border: '1px dashed',
                              borderColor: (theme) =>
                                alpha(theme.palette.text.primary, 0.18),
                              borderRadius: 1,
                              bgcolor: (theme) =>
                                alpha(theme.palette.text.primary, 0.025),
                            }}
                          >
                            {t(configurationHintKey)}
                          </Typography>
                        ) : null}

                        {module.key === 'safe_automation' ? (
                          <SafeAutomationConfigPanel
                            companyId={companyId}
                            module={module}
                            accentColor={accent}
                            disabled={configurationDisabled}
                            onSave={(config) => {
                              updateModuleMutation.mutate({
                                moduleKey: module.key,
                                data: {
                                  status:
                                    module.status === 'enabled'
                                      ? 'enabled'
                                      : 'disabled',
                                  scope: module.scope,
                                  config,
                                },
                              });
                            }}
                          />
                        ) : null}

                        {module.key === 'ai_assistant' ? (
                          <>
                            <AiAssistantPolicyPanel
                              companyId={companyId}
                              module={module}
                              safeAutomationStatus={safeAutomationModule?.status}
                              accentColor={accent}
                              disabled={configurationDisabled}
                              onSave={saveAiAssistantPolicy}
                            />
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                href={`/${ROUTES_PATH.MAIN.AI_TRACE}?company_id=${encodeURIComponent(companyId)}`}
                                variant="outlined"
                                sx={{
                                  minHeight: 36,
                                  borderRadius: 1,
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                {t('company:modules.aiAssistant.openJournal')}
                              </Button>
                              {canManageModules ? (
                                <Button
                                  href="#ai-provider-settings"
                                  variant="text"
                                  sx={{
                                    minHeight: 36,
                                    borderRadius: 1,
                                    fontSize: 12,
                                    fontWeight: 800,
                                  }}
                                >
                                  {t('company:modules.aiAssistant.openProvider')}
                                </Button>
                              ) : null}
                            </Stack>
                          </>
                        ) : null}

                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          );
        })}
      </Stack>

      {aiAssistantModule ? (
        <Box
          id="ai-trace-preview"
          ref={(node: HTMLDivElement | null) => {
            moduleRefs.current.ai_audit = node;
          }}
          sx={{ mt: 2.5 }}
        >
          <AiAuditPanel
            companyId={companyId}
            accentColor={MODULE_ACCENTS.ai_assistant}
            disabled={updateModuleMutation.isPending}
          />
        </Box>
      ) : null}

      <Notification
        open={updateModuleMutation.isError}
        title={updateModuleMutation.error?.message}
      />
    </Box>
  );
};
